import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type AppShellSkeletonProps = {
  ariaLabel?: string
  className?: string
}

/** Minimal chrome while auth state initializes. */
export function AppShellSkeleton({
  ariaLabel = 'Loading application',
  className,
}: AppShellSkeletonProps) {
  return (
    <div
      className={cn('flex min-h-screen bg-background', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <aside className="hidden w-48 shrink-0 border-r border-border p-4 md:block">
        <Skeleton className="mb-6 h-8 w-24" />
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      </aside>
      <main className="flex flex-1 flex-col p-6">
        <Skeleton className="mb-6 h-10 w-48" />
        <div className="grid grid-cols-1 gap-4 @2col:grid-cols-2 @4col:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="mt-6 h-64 w-full rounded-xl" />
      </main>
    </div>
  )
}

/** Onboarding wizard step placeholder. */
export function OnboardingStepSkeleton({
  ariaLabel = 'Loading onboarding',
  className,
}: {
  ariaLabel?: string
  className?: string
}) {
  return (
    <div
      className={cn('mx-auto w-full max-w-2xl space-y-6', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <Skeleton className="h-2 w-full rounded-full" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <div className="flex justify-between">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  )
}
